import React, { useContext, useMemo } from 'react';
import Row from 'components/Common/Row';
import CurrentUserContext from 'contexts/CurrentUserContext';
import ImpersonatorContext from 'contexts/ImpersonatorContext';
import { BonusAppContext } from 'contextProviders/BonusAppProvider';
import getBonusAppSignOffBoards from 'contextProviders/BonusAppProvider/getBonusAppSignOffBoards';
import SignOffButton from './SignOffButton';
import Owners from './Owners';
import useBoardOwners from './useBoardOwners';
import UsersContext from '../../../../contexts/UsersContext';
import { GroupsContext } from '../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';
import _ from 'lodash';
import isDefined from '../../../../isDefined';

const useOwnersGreenList = (app: V3.BonusApp) => {
  const { signedUpAndPendingUsers } = useContext(UsersContext);
  const { getUsersInGroup } = useContext(GroupsContext);

  const ownersGreenList = useMemo(() => {
    const viewerAccess = app.appViewersAccess.userIds
      .map((userId) => signedUpAndPendingUsers.find((u) => u.id === userId))
      .filter(isDefined);
    const groupAccess = app.appViewersAccess.groupIds.reduce(
      (acc, groupId) => {
        return [
          ...acc,
          ...getUsersInGroup(groupId)
            .filter((user) =>
              signedUpAndPendingUsers.some((u) => u.id === user.id),
            )
            .filter(isDefined),
        ];
      },
      [] as (UserManagement.SignedUpUser | UserManagement.PendingUser)[],
    );

    const adminAccess = app.adminUserIds
      .map((userId) => signedUpAndPendingUsers.find((u) => u.id === userId))
      .filter(isDefined);

    return _.uniqBy([...viewerAccess, ...groupAccess, ...adminAccess], 'id');
  }, [
    app.adminUserIds,
    app.appViewersAccess.groupIds,
    app.appViewersAccess.userIds,
    getUsersInGroup,
    signedUpAndPendingUsers,
  ]);

  return ownersGreenList;
};

const BonusBoardSignOff = ({
  app,
  period,
  signOffBoardNavItem,
}: {
  app: V3.BonusApp;
  period: BonusTypes.BonusPeriod;
  signOffBoardNavItem:
    | V3.Navigation.SignOffBoard
    | V3.Navigation.ApprovalsSignOffBoardTab;
}) => {
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const { owners, onOwnerAdded, onOwnerRemoved, isLoading } = useBoardOwners({
    app,
    signOffBoardNavItem,
  });
  const ownersGreenList = useOwnersGreenList(app);

  const isCurrentUserOwner = useMemo(() => {
    if (impersonatorId) {
      return owners.some((o) => o.id === impersonatorId);
    }
    return owners.some((o) => o.id === currentUserId);
  }, [currentUserId, impersonatorId, owners]);

  return (
    <Row centerAlign style={{ gap: 8 }}>
      <Owners
        isLoading={isLoading}
        owners={owners}
        onOwnerAdded={onOwnerAdded}
        onOwnerRemoved={onOwnerRemoved}
        ownersGreenList={ownersGreenList}
      />
      {period.status === 'pending sign off' && (
        <SignOffButton
          app={app}
          period={period}
          signOffBoardNavItem={signOffBoardNavItem}
          isCurrentUserOwner={isCurrentUserOwner}
        />
      )}
      <div
        style={{
          borderRight: `1px solid var(--color-grid-border)`,
          height: 24,
          width: 1,
        }}
      />
    </Row>
  );
};

const SignOffBoardNavItemProvider = ({
  board,
  app,
  period,
}: {
  board: PerformanceBoardTypes.Board;
  app: V3.BonusApp;
  period: BonusTypes.BonusPeriod;
}) => {
  const signOffBoardNavItem = useMemo(():
    | V3.Navigation.SignOffBoard
    | V3.Navigation.ApprovalsSignOffBoardTab
    | undefined => {
    // Shared with the close-period checklist (BoardSignOffItem) so both paths see
    // the same set of sign-off boards — top-level nav items AND Approvals tabs.
    return getBonusAppSignOffBoards(app).find((b) => b.boardId === board.id)
      ?.navItem;
  }, [board.id, app]);

  if (!signOffBoardNavItem) {
    return null;
  }

  return (
    <BonusBoardSignOff
      app={app}
      period={period}
      signOffBoardNavItem={signOffBoardNavItem}
    />
  );
};

/**
 * This flow is for the new bonus system, from 2025
 *
 * Take a look at {@link BonusSignOffGate} for the bonus system implemented in 2020.
 */
const BonusBoardSignOffGate = ({
  board,
}: {
  board: PerformanceBoardTypes.Board;
}) => {
  const bonusAppContext = useContext(BonusAppContext);

  /**
   * This component can be rendered from a couple different contexts.
   * It's only needed when that context is via a bonus app
   */
  if (!bonusAppContext) {
    return null;
  }

  return (
    <SignOffBoardNavItemProvider
      board={board}
      app={bonusAppContext.bonusApp}
      period={bonusAppContext.selectedBonusPeriod}
    />
  );
};

export default BonusBoardSignOffGate;
